import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui';
import { MerchantSelect } from '@/components/features/merchants/merchant-select';
import { useMerchantMap } from '@/hooks/use-merchant-map';
import { paymentLinkKeys, useSetPaymentLinkMerchantEnabled } from '@/hooks/use-payment-link';
import { ApiError } from '@/lib/api/client';
import type {
  PaymentLinkMerchantSetResponse,
  PaymentLinkMerchantStatus,
} from '@/types/payment-link';
import { SaltRevealDialog } from '@/components/features/payment-link/salt-reveal-dialog';
import { ActivateMerchantConfirm } from '@/components/features/payment-link/activate-merchant-confirm';

interface ActivateMerchantLauncherProps {
  /** Called after a successful activation so the page can open the drawer + surface the merchant. */
  onActivated: (merchantId: string) => void;
}

/**
 * OQ-8-Einstieg (Spec 497 Inc 2): aktiviert einen Merchant, der (noch) keinen freigeschalteten
 * Service hat und deshalb NICHT in der aus dem Services-Aggregat rekonstruierten Uebersicht
 * erscheint. Ablauf `merchant-select -> activate-merchant-confirm -> activation -> reveal`
 * (der Confirm ist die wiederverwendbare D-Components-Komponente). Der Merchant kommt aus dem
 * Facade-Merchant-Picker (`GET /admin/merchants`, Cap 100 — fuer v1 ausreichend; Follow-up =
 * serverseitig suchende Auswahl). Nach Erfolg: Merchant-Status-Cache seeden (sofortige Drawer-
 * Ansicht ohne "nicht provisioniert"-Flash), Reveal (falls Salt-Klartext, aus der Antwort —
 * D-Reveal), D5-Warnung falls FacadeCredentialProvisioned:false, und `onActivated`.
 */
export function ActivateMerchantLauncher({ onActivated }: ActivateMerchantLauncherProps) {
  const { t } = useTranslation('payment-link');
  const queryClient = useQueryClient();
  const { resolveMerchantName } = useMerchantMap();
  const [step, setStep] = useState<'picker' | 'confirm' | null>(null);
  const [selected, setSelected] = useState<string | undefined>(undefined);
  const [revealSalt, setRevealSalt] = useState<string | null>(null);
  const setEnabled = useSetPaymentLinkMerchantEnabled();

  function reset() {
    setStep(null);
    setSelected(undefined);
  }

  /** Close the reveal AND drop the mutation result so the cleartext does not linger in the
   *  React-Query mutation cache (M1). */
  function closeReveal() {
    setRevealSalt(null);
    setEnabled.reset();
  }

  /** Seed the merchant-status cache so the drawer shows the fresh state without a GET flash. */
  function seedMerchantStatus(res: PaymentLinkMerchantSetResponse) {
    const seeded: PaymentLinkMerchantStatus = {
      MerchantId: res.MerchantId,
      Enabled: res.Enabled,
      HasSalt: true,
      SaltRotationCount: 0,
      // explicit false = D5 broken state; absent (re-activation) → assume provisioned.
      HasFacadeCredential: res.FacadeCredentialProvisioned !== false,
      CredentialRotationCount: 0,
      ServiceCount: 0,
      ...(res.Warning ? { Warning: res.Warning } : {}),
    };
    queryClient.setQueryData(paymentLinkKeys.merchant(res.MerchantId), seeded);
  }

  function submit() {
    if (!selected) return;
    const merchantId = selected;
    setEnabled.mutate(
      { merchantId, enabled: true },
      {
        onSuccess: (res) => {
          reset();
          seedMerchantStatus(res);
          if (res.SaltCleartext) {
            setRevealSalt(res.SaltCleartext);
          } else {
            toast.success(t('toast.activated'));
          }
          if (res.FacadeCredentialProvisioned === false) {
            toast.error(t('warning.credentialMissing'));
          }
          onActivated(merchantId);
        },
        onError: (err) => {
          if (err instanceof ApiError && err.status === 503) {
            toast.error(t('retry.unavailable'));
            return;
          }
          toast.error(err instanceof Error ? err.message : t('retry.unavailable'));
        },
      },
    );
  }

  return (
    <>
      <Button onClick={() => setStep('picker')}>{t('actions.activateMerchant')}</Button>

      {/* Step 1: pick the merchant. */}
      <Dialog open={step === 'picker'} onOpenChange={(o) => (o ? setStep('picker') : reset())}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('picker.title')}</DialogTitle>
            <DialogDescription>{t('picker.body')}</DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <MerchantSelect
              value={selected}
              onChange={setSelected}
              includeAll={false}
              placeholder={t('picker.placeholder')}
            />
            <p className="text-xs text-muted-foreground">{t('picker.hint')}</p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={reset}>
              {t('confirm.cancel')}
            </Button>
            <Button onClick={() => setStep('confirm')} disabled={!selected}>
              {t('picker.submit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Step 2: the reusable activation confirm (D-Components). */}
      {selected && (
        <ActivateMerchantConfirm
          open={step === 'confirm'}
          onOpenChange={(o) => !o && setStep('picker')}
          merchantName={resolveMerchantName(selected)}
          enabled
          onConfirm={submit}
          isPending={setEnabled.isPending}
        />
      )}

      {revealSalt !== null && (
        <SaltRevealDialog
          open
          onOpenChange={(o) => !o && closeReveal()}
          saltCleartext={revealSalt}
          onConfirm={closeReveal}
        />
      )}
    </>
  );
}
